/**
 * ComplianceStreaks — Feb 2026 · r-streak-leaderboard
 *
 * A friendly monthly grid: every active teammate on the left, one cell
 * per calendar day on the right. Green = compliant, red = violation,
 * grey = skipped (weekend / holiday / leave). The teammate carrying
 * the top streak of the month gets a flame emoji next to their name;
 * everyone with a perfect month gets a golden star.
 *
 * The grid is deliberately proud rather than punitive — the sort
 * ranks the longest streak first, then most compliant days, so folks
 * who consistently show up bubble to the top.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Loader2, Flame, Star, ArrowLeft, Trophy } from "lucide-react";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const CELL_TONE = {
  compliant: "bg-emerald-500",
  violation: "bg-red-500",
  skipped: "bg-slate-200",
  pending: "bg-slate-100 border border-dashed border-slate-300",
};

export default function ComplianceStreaks() {
  const { activeWorkspaceId } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/compliance/streaks?month=${month}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Load failed")))
      .finally(() => setLoading(false));
  }, [activeWorkspaceId, month]);

  const rows = data?.grid || [];
  const topStreak = data?.top_streak || 0;
  const daysInMonth = data?.days_in_month || 30;
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const perfectCount = rows.filter((r) => r.perfect_month).length;

  return (
    <>
      <PageHeader
        eyebrow="Compliance streaks"
        title={`Streak leaderboard · ${month}`}
        description="Who's kept a clean compliance streak this month? Flame for the top run, star for a perfect month."
        testid="compliance-streaks-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/app/reports/compliance-monitor">
              <Button size="sm" variant="outline" data-testid="cs-back-btn"><ArrowLeft className="w-4 h-4 mr-2" /> Back to monitor</Button>
            </Link>
            <div>
              <Label className="text-[11px]">Month</Label>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value || currentMonth())}
                className="h-9"
                data-testid="cs-month-input"
              />
            </div>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatTile label="Top streak" value={topStreak} suffix="days" icon={Flame} tone="text-orange-600" testid="cs-stat-top" />
          <StatTile label="Perfect months" value={perfectCount} suffix="teammates" icon={Star} tone="text-amber-500" testid="cs-stat-perfect" />
          <StatTile label="Roster" value={rows.length} suffix="active" icon={Trophy} tone="text-primary" testid="cs-stat-roster" />
        </div>

        {loading ? (
          <div className="border border-border bg-background flex flex-col items-center justify-center py-16 gap-3" data-testid="cs-loading">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <div className="text-sm">Loading streaks…</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-border bg-background"><EmptyState title="No teammates in scope" description="Add employees or wait for the daily sweep to complete." /></div>
        ) : (
          <div className="border border-border bg-background overflow-x-auto" data-testid="cs-grid">
            <table className="text-xs w-full min-w-[900px]">
              <thead className="bg-secondary/40 sticky top-0">
                <tr>
                  <th className="text-left p-2 min-w-[180px]">Teammate</th>
                  <th className="text-center p-2 w-16" title="Longest streak this month">🔥 Streak</th>
                  <th className="text-center p-2 w-14" title="Compliant days">✅</th>
                  <th className="text-center p-2 w-14" title="Violation days">⚠️</th>
                  {days.map((d) => (
                    <th key={d} className="text-center p-1 w-6 font-mono text-[9px] text-muted-foreground">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-border hover:bg-secondary/20" data-testid={`cs-row-${r.user_id}`}>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold shrink-0">
                          {(r.user_name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex items-center gap-1">
                          <span className="font-medium truncate">{r.user_name}</span>
                          {r.longest_streak >= topStreak && topStreak > 0 && (
                            <Flame className="w-3.5 h-3.5 text-orange-500 shrink-0" title={`Top streak: ${topStreak} days`} />
                          )}
                          {r.perfect_month && (
                            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 shrink-0" title="Perfect month" />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-center font-mono font-semibold">{r.longest_streak}</td>
                    <td className="p-2 text-center font-mono text-emerald-700">{r.compliant_days}</td>
                    <td className="p-2 text-center font-mono text-red-600">{r.violated_days}</td>
                    {r.days.map((cell, i) => (
                      <td key={i} className="p-1 text-center">
                        <div
                          className={`w-4 h-4 mx-auto rounded-sm ${CELL_TONE[cell.state] || "bg-slate-200"}`}
                          title={`${cell.date} · ${cell.state}`}
                          data-testid={`cs-cell-${r.user_id}-${cell.date}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="border border-border bg-background p-3 flex items-center gap-4 text-xs" data-testid="cs-legend">
          <div className="font-semibold">Legend:</div>
          <LegendChip tone="compliant" label="Compliant day" />
          <LegendChip tone="violation" label="Violation" />
          <LegendChip tone="skipped" label="Skipped (leave / weekend / holiday)" />
          <LegendChip tone="pending" label="Not yet swept" />
        </div>
      </div>
    </>
  );
}

function StatTile({ label, value, suffix, icon: Icon, tone, testid }) {
  return (
    <div className="border border-border bg-background p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className={`w-3.5 h-3.5 ${tone}`} /> {label}
      </div>
      <div className={`text-3xl font-bold font-mono leading-tight mt-1 ${tone}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">{suffix}</div>
    </div>
  );
}

function LegendChip({ tone, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-3 h-3 rounded-sm ${CELL_TONE[tone]}`} />
      <span>{label}</span>
    </div>
  );
}
